import { omit } from "lodash";
import * as React from "react";
import { useSelector } from "react-redux";
import * as yup from "yup";

import { EMPTY_ARRAY } from "@/react/empty";
import { tenantIdSelector } from "@/react/features/customer/customerSlice";
import { useTable } from "@/react/components/table/useTable";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import TableComponent from "@/react/components/table/TableComponent";
import { useGetProfileQuery } from "@/react/features/vcc/profile/profileApi";

import schema from "./schema";
import { ColumnSelector as AgentColumnSelector } from "./column-selector";
import columnDefinitions from "./columnDefinitions";
import handleUpdateResponse from "./handleUpdateResponse";
import { selectTreeState } from "./slice";
import NodeState from "./types/nodeState";

import {
  useGetAgentsQuery,
  useUpdateAgentsMutation,
} from "../common/api/agentsApi";

import PageContentContainer from "@/react/features/styled/PageContentContainer";
import TableWrapper from "@/react/features/styled/TableWrapper";
import PageTitle from "@/react/components/PageTitle";

const computeNextColumnVisibility = (currentVisibility, tree) => {
  const nonGroupNodes = tree.filter((node) => !node?.isGroup);
  const nextColumnVisibility = { ...currentVisibility };
  for (const node of nonGroupNodes) {
    nextColumnVisibility[node.id] = node.nodeState === NodeState.CHECKED;
  }

  return nextColumnVisibility;
};

const AgentsPage = () => {
  const tenantName = useSelector(tenantIdSelector);
  const { data: profile } = useGetProfileQuery(tenantName);
  const columns = React.useMemo(
    () => columnDefinitions(profile?.flags),
    [columnDefinitions, profile]
  );

  const currentTreeState = useSelector(selectTreeState);
  const {
    data: agentsData,
    isFetching,
    error: apiError,
    refetch: onRefresh,
  } = useGetAgentsQuery({ tenantName });

  const [updateAgents, { isLoading: isUpdating }] = useUpdateAgentsMutation();
  const { table, filters, mutations, state } = useTable(
    agentsData ?? EMPTY_ARRAY,
    columns,
    schema,
    schema.fields.id.spec.key
  );

  // Act as source-of-truth for column visibility via setColumnVisibility (side effect).
  const { setColumnVisibility } = state;
  React.useEffect(() => {
    setColumnVisibility((columnVisibility) =>
      computeNextColumnVisibility(columnVisibility, currentTreeState)
    );
  }, [currentTreeState]);

  const {
    rowsToUpdate,
    setData,
    resetData: onCancel,
    setRowsToUpdate,
  } = mutations;
  const isDirty = rowsToUpdate.size > 0;

  // Handle API errors.
  useErrorNotification(apiError);

  const onSave = React.useCallback(() => {
    if (rowsToUpdate?.size > 0) {
      const agents = Array.from(rowsToUpdate.values());

      updateAgents({ tenantName, agents })
        .unwrap()
        .then(handleUpdateResponse({ rowsToUpdate, setRowsToUpdate, setData }))
        .catch(errorNotification);
    }
  }, [rowsToUpdate]);

  // Remove add and delete operations from the final set of possible mutations,
  // since those are not allowed by this API.
  const finalMutations = React.useMemo(
    () => omit(mutations, ["addRows", "deleteRows"]),
    [mutations]
  );

  return (
    <PageContentContainer>
      <PageTitle>Agents</PageTitle>
      <TableWrapper>
        <TableComponent
          title="Agents"
          schema={schema}
          isDirty={isDirty}
          isLoading={isFetching || isUpdating}
          onSave={onSave}
          onCancel={onCancel}
          onRefresh={onRefresh}
          table={table}
          filters={filters}
          mutations={finalMutations}
          state={state}
          renderColumnSelector={() => <AgentColumnSelector />}
          fileActions={{
            identifierColumnId: yup.reach(schema, "sso.sso_username").spec.key,
            enableUpdate: true,
            enableDownload: true,
          }}
        />
      </TableWrapper>
    </PageContentContainer>
  );
};

export default AgentsPage;
